import {dropdownList, games, trackList} from '@/data/music';
import {computed, reactive, ref} from 'vue';
import {GuessType, secondSteps} from '@/data/constants';

export interface GameRound {
	id: number;
	guesses: Guess[];
	started?: number;
	finished?: number;
}

export interface Guess {
	answer?: string | null;
	type: GuessType;
}

function getStoredData(): GameRound[] {
	const storedData = localStorage.getItem('22m9-progress');
	if (!storedData) return [];

	try {
		const rounds = JSON.parse(storedData);
		if (Array.isArray(rounds)) return rounds.filter((round: Partial<GameRound>) =>
			typeof round.id === 'number' && trackList.some(l => l.id === round.id) && Array.isArray(round.guesses));
		return [];
	} catch (e) {
		console.error(e);
		return [];
	}
}

function calculateAvailableTime(guesses: number) {
	let time = 0;
	for (let i = 0; i <= guesses && i < secondSteps.length; i++) {
		time += secondSteps[i];
	}
	return time;
}

export const progress = reactive(getStoredData());

let completedRounds = 0;
let round: number | null = null;

trackList.forEach((track, i) => {
	let playerResult = progress.find(r => r.id === track.id);
	if (!playerResult) {
		playerResult = {
			id: track.id,
			guesses: [],
		};
		progress.push(playerResult);
	}
	if (playerResult.finished) completedRounds++;
	else round ??= i;
});

export const firstRound = ref(true);
export const currentRound = ref(round ?? 0);
export const totalRounds = ref(trackList.length);
export const showShareScreen = ref(trackList.length === completedRounds);

export const currentTrack = computed(() => trackList[currentRound.value]!);
export const currentGame = computed(() => games.find(game => currentTrack.value.gameId === game.id)!);
const currentPlayerResult = computed(() => progress.find(r => r.id === currentTrack.value.id)!);

export const playerGuesses = computed(() => currentPlayerResult.value.guesses);
export const playerCorrect = computed(() => playerGuesses.value.findIndex(guess => guess.type === GuessType.CORRECT) + 1);
export const playerCorrectTime = computed(() => calculateAvailableTime(playerCorrect.value - 1));
export const roundFinished = computed(() => typeof currentPlayerResult.value.finished === 'number');
export const currentAttempt = computed(() => playerGuesses.value.length);
export const availableTime = computed(() => calculateAvailableTime(currentAttempt.value));
export const nextAttemptTime = computed(() => currentAttempt.value + 1 < secondSteps.length ? secondSteps[currentAttempt.value + 1] : null);

export function submitGuess(guess?: string) {
	if (!guess) {
		addGuess({type: GuessType.SKIP});
		return;
	}

	const lc = guess.toLowerCase().trim();
	const guessOption = dropdownList.find(l => l.title.toLowerCase() === lc);
	const guessGame = guessOption ? games.find(game => game.id === guessOption.game) : undefined;
	const correctGame = games.find(game => game.id === currentTrack.value.gameId)!;
	const correct = lc === `${currentTrack.value.answer} - ${correctGame.title}`.toLowerCase();
	const partial = guessGame && guessGame.id === currentTrack.value.gameId;

	addGuess({
		answer: guess,
		type: correct ? GuessType.CORRECT : partial ? GuessType.PARTIAL : GuessType.INCORRECT,
	});
}

function addGuess(guess: Guess) {
	const roundResult = progress.find(r => r.id === currentTrack.value.id)!;
	if (roundResult.finished) return;
	roundResult.started ??= Date.now();
	roundResult.guesses.push(guess);

	if (roundResult.guesses.length >= secondSteps.length || roundResult.guesses.some(guess => guess.type === GuessType.CORRECT))
		roundResult.finished = Date.now();

	save();
}

export function markStart() {
	const roundResult = progress.find(r => r.id === currentTrack.value.id)!;
	roundResult.started ??= Date.now();
	firstRound.value = false;
	save();
}

function save() {
	localStorage.setItem('22m9-progress', JSON.stringify(progress));
}
